import { CommonModule } from '@angular/common';
import { Component, Inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CmModalSettings } from '@ats/cm/ui';
import { AtsNotificationService } from '@ats/notifications';
import {
	LOC_INJECTOR,
	LocalizationProvider,
	LocalizationService,
	LocalizationVM,
} from '@cm/core/localization';
import { SafeHtmlPipe } from '@cm/shared/ui/utilities';
import { of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { LocalizationDialogComponent } from '../localization-dialog/localization-dialog.component';
import { inliningLocalizationLocs, InliningLocType } from './inlining-localization.locs';

type CustomStyle = {
	[key: string]: string;
};
@Component({
	selector: 'cm-inlining-localization',
	templateUrl: './inlining-localization.component.html',
	styleUrls: ['./inlining-localization.component.scss'],
	imports: [CommonModule, MatDialogModule, SafeHtmlPipe],
	providers: [LocalizationProvider(inliningLocalizationLocs)],
})
export class InliningLocalizationComponent {
	readonly locName = input.required<string>();
	readonly customInliningStyle = input<CustomStyle | null>(null);
	readonly noValueDisplay = toSignal<string>(
		this.locService.findValue$(this.locs.CMSystemLocalizationNoValueSet)
	);
	readonly loc = toSignal<LocalizationVM | null>(
		toObservable<string>(this.locName).pipe(
			switchMap(locName => (!!locName ? this.locService.find$(locName) : of(null)))
		)
	);

	constructor(
		@Inject(LOC_INJECTOR) public readonly locs: InliningLocType,
		private readonly dialog: MatDialog,
		private readonly locService: LocalizationService,
		private readonly notificationService: AtsNotificationService
	) {}

	localize(e: Event): void {
		e.preventDefault();
		e.stopPropagation();

		this.dialog
			.open(LocalizationDialogComponent, {
				...CmModalSettings.Medium,
				autoFocus: '.loc-dialog-first',
				data: { loc: this.loc() },
			})
			.afterClosed()
			.pipe(
				switchMap(hasCodeChanges =>
					hasCodeChanges
						? this.locService.findValue$(
								this.locs.CMSystemLocalizationDialogModificationWarning
						  )
						: of(null)
				),
				take(1)
			)
			.subscribe(message => {
				if (message) {
					this.notificationService.popInfo({
						id: 'possible-loc-changes',
						message,
					});
				}
			});
	}
}
